import axios from "@/libs/axios";
import type { Webhook } from "@/types/webhook.types";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import { useQuery } from "@tanstack/vue-query";
import { AxiosResponse } from "axios";
import { defineStore } from "pinia";

interface State {
    isLoadingWebhooks: boolean;
    isLoadingWebhook: boolean;
    webhooks: Webhook[];
}
export const useWebhooksStore = defineStore("webhooks", {
    state: (): State => ({
        isLoadingWebhooks: false,
        isLoadingWebhook: false,
        webhooks: [],
    }),
    actions: {
        fetchWebhooks(requestQueryBuilder?: RequestQueryBuilder): void {
            useQuery<Pick<AxiosResponse<Array<Omit<Webhook, "headers"> & { headers: string }>>, "data">>(
                ["webhooks", requestQueryBuilder?.queryObject],
                () => {
                    this.isLoadingWebhooks = true;
                    return axios.get<Array<Omit<Webhook, "headers"> & { headers: string }>>(
                        `${process.env.VUE_APP_PUBLIC_API}/webhooks`,
                        {
                            params: requestQueryBuilder?.queryObject,
                        }
                    );
                },
                {
                    //staleTime: 300000, // 5min
                    refetchOnWindowFocus: false,
                    placeholderData: { data: [] },
                    //onSuccess: (response) => {
                    //    this.webhooks = response.data.map((webhook) => ({
                    //        ...webhook,
                    //        headers: webhook.headers ? JSON.parse(webhook.headers) : undefined,
                    //    }));
                    //},
                    onSuccess: (response) => (this.webhooks = response.data),
                    onSettled: () => (this.isLoadingWebhooks = false),
                }
            );
        },
        createWebhook(webhook: Webhook): Promise<Webhook> {
            this.isLoadingWebhook = true;

            return axios
                .post(`${process.env.VUE_APP_PUBLIC_API}/webhooks`, {
                    ...webhook,
                    headers: webhook.headers ? JSON.parse(webhook.headers) : null, // entity is not fully correct, so using "{}" instead of null.
                })
                .then(({ data }) => {
                    this.webhooks.unshift(data);

                    return data;
                })
                .finally(() => (this.isLoadingWebhook = false));
        },
        updateWebhook(webhookId: number, partialWebhook: Partial<Webhook>): Promise<Webhook> {
            this.isLoadingWebhook = true;

            const index = this.webhooks.findIndex((wh: Webhook) => wh.id === webhookId);

            const webhook: Webhook = {
                ...this.webhooks[index],
                ...partialWebhook,
            };

            return axios
                .patch(`${process.env.VUE_APP_PUBLIC_API}/webhooks/${webhookId}`, {
                    ...webhook,
                    headers: webhook.headers ? JSON.parse(webhook.headers) : null, // entity is not fully correct, so using "{}" instead of null.
                })
                .then(() => {
                    this.webhooks.splice(index, 1, webhook);

                    return webhook;
                })
                .finally(() => (this.isLoadingWebhook = false));
        },
        deleteWebhook(webhookId: number): Promise<number> {
            this.isLoadingWebhook = true;

            return axios
                .delete(`${process.env.VUE_APP_PUBLIC_API}/webhooks/${webhookId}`)
                .then(({ data }) => {
                    const index = this.webhooks.findIndex((wh: Webhook) => wh.id === webhookId);
                    this.webhooks.splice(index, 1);

                    return data.affected;
                })
                .finally(() => (this.isLoadingWebhook = false));
        },
    },
    getters: {
        getWebhookById(): (webhookId: number) => Webhook | undefined {
            return (webhookId: number) => this.webhooks.find((wh: Webhook) => wh.id === webhookId);
        },
    },
});
